"use client";

import { useState, useCallback, useRef } from "react";
import { useRouter } from "next/router";
import { useQueryClient } from "@tanstack/react-query";

import {
  createPaypalOrder,
  createPaypalPaymentMethod,
} from "@/networking/fast-checkout/paypal.api";
import { CartResponse, updateCartAttributes } from "@/networking/carts.api";
import { checkoutCart } from "@/networking/checkout.api";
import { updateAddress, type Address } from "@/networking/addresses.api";
import { updateShippingMethod } from "@/networking/shipping.api";
import { useLoadingOverlay } from "@/components/providers/CheckoutLoading";
import { toPaymentAmount } from "@/lib/utils";
import { checkoutRedirect } from "@/lib/checkout-redirect";

/* ---------- helpers ---------- */
interface UseApplePayPalCheckoutProps {
  onSuccess?: () => Promise<void>;
  onError?: (error: Error) => void;
  onRedirect?: (url: string) => void;
  paymentMethodsIncludes?: string | null;
  deviceData?: string;
  kountSessionId?: string;
}

// PayPal Apple Pay specific types
export interface PayPalApplePayConfig {
  isEligible: boolean;
  countryCode: string;
  merchantCapabilities: ApplePayJS.ApplePayMerchantCapability[];
  supportedNetworks: string[];
}

interface PayPalApplePayInstance {
  config: () => Promise<PayPalApplePayConfig>;
  validateMerchant: (params: {
    validationUrl: string;
    displayName?: string;
  }) => Promise<{ merchantSession: unknown }>;
  confirmOrder: (params: {
    orderId: string;
    token: ApplePayJS.ApplePayPaymentToken;
    billingContact?: ApplePayJS.ApplePayPaymentContact;
    shippingContact?: ApplePayJS.ApplePayPaymentContact;
  }) => Promise<{ approveApplePayPayment?: { id: string } }>;
}

declare global {
  interface PayPalNamespace {
    Applepay?: () => PayPalApplePayInstance;
  }
}

type CartQueryData = { cart: CartResponse["cart"] };

type ApiShippingMethod =
  CartResponse["cart"]["available_shipping_methods"] extends Array<infer T>
    ? T
    : never;

function toApplePayShippingMethod(
  m: ApiShippingMethod,
  amount: (s: string | number) => string,
): ApplePayJS.ApplePayShippingMethod {
  const detail =
    typeof (m as { delivery_time_estimate?: string }).delivery_time_estimate ===
    "string"
      ? (m as { delivery_time_estimate: string }).delivery_time_estimate
      : "";
  return {
    label: (m as { title: string }).title,
    amount: amount(String((m as { price: number }).price)),
    detail,
    identifier: String((m as { id: string | number }).id),
  };
}

type ApplePayPRMerchantSession = object;
interface ApplePayMerchantValidationEvent {
  validationURL: string;
  complete: (
    merchantSession: ApplePayPRMerchantSession | null,
  ) => Promise<void> | void;
}

type ApplePayPRRequest = PaymentRequest & {
  onmerchantvalidation?: (e: ApplePayMerchantValidationEvent) => void;
};

const isSecureAppleContext = () => {
  if (typeof window === "undefined") return false;
  return window.isSecureContext;
};

/* ---------- hook ---------- */
export const useApplePayPalCheckout = ({
  onSuccess,
  onError,
  onRedirect,
  paymentMethodsIncludes,
  deviceData,
  kountSessionId,
}: UseApplePayPalCheckoutProps = {}) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { showCheckoutLoader, hideCheckoutLoader } = useLoadingOverlay();

  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedShippingMethodId, setSelectedShippingMethodId] = useState<
    string | null
  >(null);

  const isStartingRef = useRef(false);

  const getEffectiveTotal = useCallback(
    (cart: CartQueryData["cart"]): number => {
      if (cart.points_applied && cart.points_applied > 0) {
        return cart.remaining_cart_amount_after_points ?? cart.amount_total;
      }
      return cart.amount_total;
    },
    [],
  );

  const writeContactsToCart = useCallback(
    async ({
      cartToken,
      email,
      phone,
      billTo,
      shipTo,
      recomputeTotals,
    }: {
      cartToken: string;
      email?: string | null;
      phone?: string | null;
      billTo?: ApplePayJS.ApplePayPaymentContact | null;
      shipTo?: ApplePayJS.ApplePayPaymentContact | null;
      recomputeTotals: boolean;
    }): Promise<CartQueryData> => {
      const ops: Promise<unknown>[] = [];

      if (email || phone) {
        ops.push(
          updateCartAttributes({
            cartToken,
            ...(email ? { email } : {}),
            ...(phone ? { phone } : {}),
          }).catch(() => {}),
        );
      }

      const toAddr = (
        c: ApplePayJS.ApplePayPaymentContact,
      ): Address["address"] => ({
        name: [c.givenName?.trim(), c.familyName?.trim()]
          .filter(Boolean)
          .join(" "),
        address1: c.addressLines?.[0] ?? "",
        address2: c.addressLines?.[1] ?? "",
        city: c.locality ?? "",
        state: c.administrativeArea ?? "",
        postal_code: c.postalCode ?? "",
        country_code: c.countryCode ?? "US",
      });

      const hasUsefulLocationData = (
        contact: ApplePayJS.ApplePayPaymentContact,
      ): boolean => {
        return !!(
          contact.countryCode ||
          contact.postalCode ||
          (contact.administrativeArea && contact.locality)
        );
      };

      if (billTo?.addressLines?.length) {
        ops.push(
          updateAddress(
            { type: "bill_to", address: toAddr(billTo) },
            cartToken,
          ).catch(() => {}),
        );
      } else if (billTo && hasUsefulLocationData(billTo)) {
        ops.push(
          updateAddress(
            { type: "bill_to", address: toAddr(billTo) },
            cartToken,
          ).catch(() => {}),
        );
      }

      if (shipTo?.addressLines?.length) {
        ops.push(
          updateAddress(
            { type: "ship_to", address: toAddr(shipTo) },
            cartToken,
          ).catch(() => {}),
        );
      } else if (shipTo && hasUsefulLocationData(shipTo)) {
        ops.push(
          updateAddress(
            { type: "ship_to", address: toAddr(shipTo) },
            cartToken,
          ).catch(() => {}),
        );
      }

      await Promise.all(ops);

      const prev = queryClient.getQueryData([
        "cart",
        cartToken,
        paymentMethodsIncludes ?? null,
      ]) as CartQueryData | undefined;
      if (prev?.cart && email) {
        queryClient.setQueryData(
          ["cart", cartToken, paymentMethodsIncludes ?? null],
          {
            cart: { ...prev.cart, email },
          } as CartQueryData,
        );
      }

      if (!recomputeTotals) {
        const local =
          (queryClient.getQueryData([
            "cart",
            cartToken,
            paymentMethodsIncludes ?? null,
          ]) as CartQueryData | undefined) ??
          ((await queryClient.fetchQuery({
            queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
          })) as CartQueryData);
        return local;
      }

      await queryClient.invalidateQueries({
        queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
      });
      return (await queryClient.fetchQuery({
        queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
      })) as CartQueryData;
    },
    [queryClient, paymentMethodsIncludes],
  );

  // ========== Payment Request API (Chrome/Firefox QR) ==========
  const handlePaymentRequestApplePay = useCallback(
    async (
      cartTokenLocal: string,
      cartLocal: CartQueryData["cart"],
      applePayConfig: PayPalApplePayConfig,
    ) => {
      // Build Payment Request. PayPal is the Apple Pay merchant — Apple
      // validates against PayPal's Apple Pay merchant registration
      // (`merchant.paypal`) and PayPal's SDK handles merchant validation +
      // order confirmation below.
      const methodData = [
        {
          supportedMethods: "https://apple.com/apple-pay",
          data: {
            merchantIdentifier: "merchant.paypal",
            countryCode: applePayConfig.countryCode,
            currencyCode: cartLocal.currency_code,
            supportedNetworks: applePayConfig.supportedNetworks,
            merchantCapabilities: applePayConfig.merchantCapabilities,
          },
        },
      ];

      const details = {
        displayItems: [
          {
            label: "Subtotal",
            amount: {
              currency: cartLocal.currency_code,
              value: toPaymentAmount(
                cartLocal.sub_total,
                cartLocal.currency_decimal_places,
              ),
            },
          },
          {
            label: "Shipping",
            amount: {
              currency: cartLocal.currency_code,
              value: toPaymentAmount(
                cartLocal.shipping_total,
                cartLocal.currency_decimal_places,
              ),
            },
          },
          {
            label: "Tax",
            amount: {
              currency: cartLocal.currency_code,
              value: toPaymentAmount(
                cartLocal.tax_total,
                cartLocal.currency_decimal_places,
              ),
            },
          },
        ],
        total: {
          label: cartLocal.company?.name || "Total",
          amount: {
            currency: cartLocal.currency_code,
            value: toPaymentAmount(
              getEffectiveTotal(cartLocal),
              cartLocal.currency_decimal_places,
            ),
          },
        },
      };

      const req: ApplePayPRRequest = new PaymentRequest(methodData, details);

      // Merchant validation: PayPal's JS SDK calls its own
      // GetApplePayMerchantSession GraphQL endpoint.
      req.onmerchantvalidation = async (e: ApplePayMerchantValidationEvent) => {
        try {
          const paypal =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).paypal_checkout_global || (window as any).paypal;
          const applepay = paypal?.Applepay();

          if (!applepay) {
            throw new Error("PayPal Apple Pay SDK not available");
          }

          const validateResult = await applepay.validateMerchant({
            validationUrl: e.validationURL,
            displayName: cartLocal.company?.name,
          });

          await e.complete(
            validateResult.merchantSession as ApplePayPRMerchantSession,
          );
        } catch (err) {
          try {
            await e.complete(null);
          } catch {
            /* noop */
          }
          throw err instanceof Error
            ? err
            : new Error("Apple Pay merchant validation failed");
        }
      };

      // Show Apple UI (QR code on Chrome/Firefox)
      const resp: PaymentResponse = await req.show();

      try {
        // Extract payment details
        type ApplePayPRDetails = {
          shippingContact?: ApplePayJS.ApplePayPaymentContact;
          billingContact?: ApplePayJS.ApplePayPaymentContact;
          payerEmail?: string;
          token?: ApplePayJS.ApplePayPaymentToken;
        };
        const applePayDetails: ApplePayPRDetails = (resp.details ||
          {}) as ApplePayPRDetails;
        const shippingContact = applePayDetails.shippingContact;
        const billingContact = applePayDetails.billingContact;
        const payerEmail =
          applePayDetails.payerEmail ||
          shippingContact?.emailAddress ||
          billingContact?.emailAddress ||
          null;
        const phone =
          shippingContact?.phoneNumber ?? billingContact?.phoneNumber ?? null;

        // Write contacts to cart
        try {
          await writeContactsToCart({
            cartToken: cartTokenLocal,
            email: payerEmail,
            phone,
            billTo: billingContact ?? null,
            shipTo: shippingContact ?? null,
            recomputeTotals: true,
          });
          await queryClient.invalidateQueries({
            queryKey: ["cart", cartTokenLocal, paymentMethodsIncludes ?? null],
          });
        } catch {
          // continue even if contact write fails
        }

        // Get fresh cart
        const fresh = (await queryClient.fetchQuery({
          queryKey: ["cart", cartTokenLocal, paymentMethodsIncludes ?? null],
        })) as CartQueryData;

        if (fresh.cart.valid_for_checkout_pre_payment === false) {
          await resp.complete("fail");
          throw new Error("Cart not ready for checkout. Please try again.");
        }

        // PayPal JS SDK handles decryption via confirmOrder.
        const createOrderResponse = await createPaypalOrder({
          cartToken: cartTokenLocal,
          amount: toPaymentAmount(
            getEffectiveTotal(fresh.cart),
            fresh.cart.currency_decimal_places,
          ),
          description: `${fresh.cart.company?.name} Order`,
          fundingSource: "apple_pay",
          isPayLater: false,
        });

        if (!createOrderResponse.success) {
          await resp.complete("fail");
          throw new Error("Order creation failed");
        }

        const paypal =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).paypal_checkout_global || (window as any).paypal;
        const applepay = paypal?.Applepay();

        if (!applepay || !applePayDetails.token) {
          await resp.complete("fail");
          throw new Error("PayPal Apple Pay SDK not available");
        }

        const confirmResult = await applepay.confirmOrder({
          orderId: createOrderResponse.order_id,
          token: applePayDetails.token,
          billingContact: billingContact,
          shippingContact: shippingContact,
        });

        await createPaypalPaymentMethod({
          cartToken: cartTokenLocal,
          orderId: createOrderResponse.order_id,
          authorizationId: confirmResult.approveApplePayPayment?.id ?? "",
        });

        await resp.complete("success");

        showCheckoutLoader("Processing your order...");

        // Checkout
        const result = await checkoutCart(cartTokenLocal, {
          deviceData,
          kountSessionId,
        });
        if (onSuccess) await onSuccess();

        checkoutRedirect(result, cartTokenLocal);
      } catch (err) {
        hideCheckoutLoader();
        try {
          await resp.complete("fail");
        } catch {
          /* noop */
        }
        throw err;
      }
    },
    [
      getEffectiveTotal,
      queryClient,
      paymentMethodsIncludes,
      onSuccess,
      onRedirect,
      router,
      writeContactsToCart,
      showCheckoutLoader,
      hideCheckoutLoader,
      deviceData,
      kountSessionId,
    ],
  );

  // ========== Main handler ==========
  const handleApplePayPalCheckout = useCallback(
    async (cartToken: string, applePayConfig: PayPalApplePayConfig) => {
      if (!isSecureAppleContext()) {
        onError?.(new Error("Apple Pay isn't available on this page."));
        return;
      }
      if (isStartingRef.current) return;

      isStartingRef.current = true;
      setIsProcessing(true);

      try {
        const cached = queryClient.getQueryData([
          "cart",
          cartToken,
          paymentMethodsIncludes ?? null,
        ]) as CartQueryData | undefined;
        let cart = cached?.cart;
        if (!cart) throw new Error("No cart data available");

        // Determine runtime environment
        const ApplePaySessionCtor = window.ApplePaySession;
        const canUsePaymentRequest = "PaymentRequest" in window;

        // If no ApplePaySession but PaymentRequest is available, use Payment Request API (Chrome/Firefox QR)
        if (!ApplePaySessionCtor && canUsePaymentRequest) {
          await handlePaymentRequestApplePay(cartToken, cart, applePayConfig);
          return;
        }

        // Continue with Safari path below
        if (!ApplePaySessionCtor) {
          throw new Error("Apple Pay not available");
        }

        const isDigitalOnly = cart.digital_only === true;
        const hasSubscription =
          cart.items?.some((i) => i.subscription || i.subscription_only) ===
          true;

        const availableShippingMethods =
          cart.available_shipping_methods?.slice() ?? [];

        if (!isDigitalOnly && availableShippingMethods.length > 0) {
          let chosenId =
            selectedShippingMethodId ?? cart.shipping_method?.id?.toString();

          if (!chosenId && availableShippingMethods[0]) {
            chosenId = String(availableShippingMethods[0].id);
            setSelectedShippingMethodId(chosenId);
            await updateShippingMethod({
              cartToken,
              shippingMethodId: availableShippingMethods[0].id,
            });
            await queryClient.invalidateQueries({
              queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
            });
            const updatedCartData = (await queryClient.fetchQuery({
              queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
            })) as CartQueryData;
            cart = updatedCartData.cart;
          }
        }

        const shippingMethods = isDigitalOnly
          ? []
          : (cart.available_shipping_methods ?? []).map((m) =>
              toApplePayShippingMethod(
                m as ApiShippingMethod,
                (v: string | number) =>
                  toPaymentAmount(
                    typeof v === "number" ? v : parseFloat(String(v ?? "0")),
                    cart.currency_decimal_places,
                  ),
              ),
            );

        const formattedTotal = toPaymentAmount(
          getEffectiveTotal(cart),
          cart.currency_decimal_places,
        );

        const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
          countryCode: applePayConfig.countryCode,
          merchantCapabilities: applePayConfig.merchantCapabilities,
          supportedNetworks: applePayConfig.supportedNetworks,
          currencyCode: cart.currency_code,
          requiredShippingContactFields: isDigitalOnly
            ? ["email", "phone"]
            : ["postalAddress", "name", "email", "phone"],
          requiredBillingContactFields: [
            "postalAddress",
            "name",
            "email",
            "phone",
          ],
          shippingMethods: shippingMethods,
          shippingType: isDigitalOnly ? undefined : "shipping",
          lineItems: [
            {
              label: "Subtotal",
              amount: toPaymentAmount(
                cart.sub_total,
                cart.currency_decimal_places,
              ),
              type: "final",
            },
            {
              label: "Shipping",
              amount: toPaymentAmount(
                cart.shipping_total,
                cart.currency_decimal_places,
              ),
              type: "final",
            },
            {
              label: "Tax",
              amount: toPaymentAmount(
                cart.tax_total,
                cart.currency_decimal_places,
              ),
              type: "final",
            },
          ],
          total: {
            label: cart.company?.name || "Total",
            type: "final",
            amount: formattedTotal,
          },
        };

        if (hasSubscription) {
          const recurringRequest: ApplePayJS.ApplePayRecurringPaymentRequest = {
            paymentDescription: `${
              cart.company?.name || "Subscription"
            } - Recurring Payment`,
            regularBilling: {
              label: cart.company?.name || "Subscription",
              amount: toPaymentAmount(
                getEffectiveTotal(cart),
                cart.currency_decimal_places,
              ),
              paymentTiming: "recurring",
              recurringPaymentStartDate: new Date(),
            },
            managementURL: `${window.location.origin}/account/subscriptions`,
          };

          (
            paymentRequest as ApplePayJS.ApplePayPaymentRequest & {
              recurringPaymentRequest?: ApplePayJS.ApplePayRecurringPaymentRequest;
            }
          ).recurringPaymentRequest = recurringRequest;
        }

        const session = new ApplePaySession(4, paymentRequest);

        // PayPal JS SDK drives merchant validation and order confirmation.
        const paypal =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).paypal_checkout_global || (window as any).paypal;
        const applepay: PayPalApplePayInstance | undefined = paypal?.Applepay();

        if (!applepay) {
          throw new Error("PayPal Apple Pay SDK not available");
        }

        session.onvalidatemerchant = async (
          event: ApplePayJS.ApplePayValidateMerchantEvent,
        ) => {
          try {
            const validateResult = await applepay.validateMerchant({
              validationUrl: event.validationURL,
              displayName: cart.company?.name,
            });
            session.completeMerchantValidation(validateResult.merchantSession);
          } catch (err) {
            console.error("Apple Pay merchant validation failed:", err);
            session.abort();
            onError?.(new Error("Apple Pay validation failed"));
          }
        };

        session.onshippingcontactselected = async (
          e: ApplePayJS.ApplePayShippingContactSelectedEvent,
        ) => {
          try {
            const updated = await writeContactsToCart({
              cartToken,
              email: e.shippingContact.emailAddress ?? null,
              phone: e.shippingContact.phoneNumber ?? null,
              billTo: null,
              shipTo: e.shippingContact,
              recomputeTotals: !isDigitalOnly,
            });

            const available =
              updated.cart.available_shipping_methods?.slice() ?? [];

            if (!isDigitalOnly && available.length === 0) {
              session.completeShippingContactSelection(
                ApplePaySession.STATUS_FAILURE,
                [],
                {
                  label: "No shipping available",
                  amount: toPaymentAmount(
                    getEffectiveTotal(cart),
                    cart.currency_decimal_places,
                  ),
                  type: "final",
                },
                [],
              );
              return;
            }

            let chosenId =
              selectedShippingMethodId ??
              updated.cart.shipping_method?.id?.toString();

            if (
              !isDigitalOnly &&
              !chosenId &&
              available.length > 0 &&
              available[0]
            ) {
              chosenId = String(available[0].id);
              setSelectedShippingMethodId(chosenId);
              await updateShippingMethod({
                cartToken,
                shippingMethodId: available[0].id,
              });
              await queryClient.invalidateQueries({
                queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
              });
            }

            const finalCart = (await queryClient.fetchQuery({
              queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
            })) as CartQueryData;

            session.completeShippingContactSelection(
              ApplePaySession.STATUS_SUCCESS,
              isDigitalOnly
                ? []
                : (finalCart.cart.available_shipping_methods ?? []).map((m) =>
                    toApplePayShippingMethod(
                      m as ApiShippingMethod,
                      (v: string | number) =>
                        toPaymentAmount(
                          typeof v === "number"
                            ? v
                            : parseFloat(String(v ?? "0")),
                          finalCart.cart.currency_decimal_places,
                        ),
                    ),
                  ),
              {
                label: cart.company?.name || "Total",
                amount: toPaymentAmount(
                  getEffectiveTotal(finalCart.cart),
                  finalCart.cart.currency_decimal_places,
                ),
                type: "final",
              },
              [
                {
                  label: "Subtotal",
                  amount: toPaymentAmount(
                    finalCart.cart.sub_total,
                    finalCart.cart.currency_decimal_places,
                  ),
                  type: "final",
                },
                {
                  label: "Shipping",
                  amount: toPaymentAmount(
                    finalCart.cart.shipping_total,
                    finalCart.cart.currency_decimal_places,
                  ),
                  type: "final",
                },
                {
                  label: "Tax",
                  amount: toPaymentAmount(
                    finalCart.cart.tax_total,
                    finalCart.cart.currency_decimal_places,
                  ),
                  type: "final",
                },
              ],
            );
          } catch (err) {
            session.completeShippingContactSelection(
              ApplePaySession.STATUS_FAILURE,
              [],
              {
                label: "Error",
                amount: toPaymentAmount(
                  getEffectiveTotal(cart),
                  cart.currency_decimal_places,
                ),
                type: "final",
              },
              [],
            );
            onError?.(
              err instanceof Error
                ? err
                : new Error("Shipping selection failed"),
            );
          }
        };

        session.onshippingmethodselected = async (
          e: ApplePayJS.ApplePayShippingMethodSelectedEvent,
        ) => {
          try {
            setSelectedShippingMethodId(e.shippingMethod.identifier);
            await updateShippingMethod({
              cartToken,
              shippingMethodId: e.shippingMethod.identifier,
            });
            await queryClient.invalidateQueries({
              queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
            });
            const updated = (await queryClient.fetchQuery({
              queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
            })) as CartQueryData;

            session.completeShippingMethodSelection(
              ApplePaySession.STATUS_SUCCESS,
              {
                label: cart.company?.name || "Total",
                amount: toPaymentAmount(
                  getEffectiveTotal(updated.cart),
                  updated.cart.currency_decimal_places,
                ),
                type: "final",
              },
              [
                {
                  label: "Subtotal",
                  amount: toPaymentAmount(
                    updated.cart.sub_total,
                    updated.cart.currency_decimal_places,
                  ),
                  type: "final",
                },
                {
                  label: "Shipping",
                  amount: toPaymentAmount(
                    updated.cart.shipping_total,
                    updated.cart.currency_decimal_places,
                  ),
                  type: "final",
                },
                {
                  label: "Tax",
                  amount: toPaymentAmount(
                    updated.cart.tax_total,
                    updated.cart.currency_decimal_places,
                  ),
                  type: "final",
                },
              ],
            );
          } catch {
            session.completeShippingMethodSelection(
              ApplePaySession.STATUS_FAILURE,
              {
                label: "Error",
                amount: toPaymentAmount(
                  getEffectiveTotal(cart),
                  cart.currency_decimal_places,
                ),
                type: "final",
              },
              [],
            );
          }
        };

        session.onpaymentauthorized = async (
          event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
        ) => {
          try {
            await writeContactsToCart({
              cartToken,
              email:
                event.payment.shippingContact?.emailAddress ??
                event.payment.billingContact?.emailAddress ??
                null,
              phone:
                event.payment.shippingContact?.phoneNumber ??
                event.payment.billingContact?.phoneNumber ??
                null,
              billTo: event.payment.billingContact ?? null,
              shipTo: event.payment.shippingContact ?? null,
              recomputeTotals: true,
            });

            await queryClient.invalidateQueries({
              queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
            });

            const freshCart = (await queryClient.fetchQuery({
              queryKey: ["cart", cartToken, paymentMethodsIncludes ?? null],
            })) as CartQueryData;

            if (freshCart.cart.valid_for_checkout_pre_payment === false) {
              session.completePayment(ApplePaySession.STATUS_FAILURE);
              onError?.(
                new Error("Cart not ready for checkout. Please try again."),
              );
              return;
            }

            // PayPal JS SDK creates the order and confirms it.
            const createOrderResponse = await createPaypalOrder({
              cartToken,
              amount: toPaymentAmount(
                getEffectiveTotal(freshCart.cart),
                freshCart.cart.currency_decimal_places,
              ),
              description: `${freshCart.cart.company?.name} Order`,
              fundingSource: "apple_pay",
              isPayLater: false,
            });

            if (!createOrderResponse.success) {
              throw new Error("Order creation failed");
            }

            const confirmResult = await applepay.confirmOrder({
              orderId: createOrderResponse.order_id,
              token: event.payment.token,
              billingContact: event.payment.billingContact,
              shippingContact: event.payment.shippingContact,
            });

            await createPaypalPaymentMethod({
              cartToken,
              orderId: createOrderResponse.order_id,
              authorizationId: confirmResult.approveApplePayPayment?.id ?? "",
            });

            session.completePayment(ApplePaySession.STATUS_SUCCESS);

            showCheckoutLoader("Processing your order...");

            const result = await checkoutCart(cartToken, {
              deviceData,
              kountSessionId,
            });

            if (onSuccess) await onSuccess();

            checkoutRedirect(result, cartToken);
          } catch (err) {
            hideCheckoutLoader();
            console.error("=== PAYMENT AUTHORIZATION FAILED ===", err);
            try {
              session.completePayment(ApplePaySession.STATUS_FAILURE);
            } catch (completeErr) {
              console.error("Could not complete payment session:", completeErr);
            }
            onError?.(err instanceof Error ? err : new Error("Payment failed"));
          }
        };

        session.oncancel = () => {
          setIsProcessing(false);
          isStartingRef.current = false;
        };

        session.begin();
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error("Apple Pay failed"));
      } finally {
        isStartingRef.current = false;
        setIsProcessing(false);
      }
    },
    [
      getEffectiveTotal,
      onError,
      onRedirect,
      onSuccess,
      paymentMethodsIncludes,
      queryClient,
      router,
      selectedShippingMethodId,
      writeContactsToCart,
      showCheckoutLoader,
      hideCheckoutLoader,
      handlePaymentRequestApplePay,
      deviceData,
      kountSessionId,
    ],
  );

  return {
    isProcessing,
    handleApplePayPalCheckout,
  };
};
